import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import { loginObservable } from '../common/observable.js';
import { InitState, LocalStorageText } from '../constants/common.js';
import GuardedRoute from '../pages/authen/GuardedRoute.js';
import AppLayout from '../layouts/AppLayout';
import SaleLayout from '../layouts/sale/SaleLayout';
import MotoLayout from '../layouts/MotoAuthLayout';
import MotoDefaultLayout from '../layouts/MotoDefaultLayout';
import VatChatOtoLayout from '../pages/insurance-car/Layout';
import InspectionLibertyLayout from '../layouts/InspectionLibertyLayout';
import HealthInsuranceLayout from '../layouts/HealthInsuranceLayout';
import CommonLayout from '../layouts/CommonLayout.js';

// Nhóm các lazy load theo từng module/feature để tối ưu code splitting
// Home pages
const HomePage = lazy(() => import(/* webpackChunkName: "home" */ '../pages/home/HomePage'));
const HomeCustomerPage = lazy(() =>
  import(/* webpackChunkName: "home" */ '../pages/home/HomeCustomerPage')
);
const HomeSalePage = lazy(() =>
  import(/* webpackChunkName: "home" */ '../pages/home/HomeSalePage.js')
);

// Claim pages
const ClaimPage = lazy(() =>
  import(/* webpackChunkName: "claim-base" */ '../pages/claim/ClaimPage')
);
const ClaimDetail = lazy(() =>
  import(/* webpackChunkName: "claim-base" */ '../pages/claim/ClaimDetail.js')
);
const ClaimInfo = lazy(() =>
  import(/* webpackChunkName: "claim-base" */ '../pages/claim/ClaimInfo.js')
);
const ClaimInfoMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/ClaimInfoMomo.js')
);
const ClaimInfo2WheelMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/ClaimInfo2WheelMomo.js')
);
const ClaimDetailMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/ClaimDetailMomo.js')
);
const Claim2WheelDetailMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/Claim2WheelDetailMomo.js')
);
const ClaimGuide = lazy(() =>
  import(/* webpackChunkName: "claim-base" */ '../pages/claim/ClaimGuide.js')
);
const CreateClaimPage = lazy(() =>
  import(/* webpackChunkName: "claim-create" */ '../pages/claim/CreateClaim.js')
);
const CreateClaimMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/CreateClaimMomo.js')
);
const CreateClaim2WheelMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/CreateClaim2WheelMomo.js')
);

// Contract pages
const ContractPage = lazy(() =>
  import(/* webpackChunkName: "contract-base" */ '../pages/contract/ContractPage')
);
const ContractDetail = lazy(() =>
  import(/* webpackChunkName: "contract-base" */ 'components/Contract/Detail.js')
);

// Account pages
const AccountPage = lazy(() =>
  import(/* webpackChunkName: "account" */ '../pages/account/AccountPage')
);
const ConfirmEmail = lazy(() =>
  import(/* webpackChunkName: "account" */ '../pages/account/ConfirmEmail')
);
const VerifyEmail = lazy(() =>
  import(/* webpackChunkName: "account" */ '../pages/account/VerifyEmail')
);
const UserInfo = lazy(() => import(/* webpackChunkName: "account" */ '../pages/account/UserInfo'));
const Password = lazy(() => import(/* webpackChunkName: "account" */ '../pages/account/Password'));
const NotificationPage = lazy(() =>
  import(/* webpackChunkName: "account" */ '../pages/notification/NotificationPage')
);

// Auth pages
const LoginPage = lazy(() => import(/* webpackChunkName: "auth" */ '../pages/login/LoginPage.js'));
const ForgotPasswordPage = lazy(() =>
  import(/* webpackChunkName: "auth" */ '../pages/forgot-password/ForgotPasswordPage.js')
);
const ChangePasswordPage = lazy(() =>
  import(/* webpackChunkName: "auth" */ '../pages/change-password/ChangePasswordPage.js')
);
const ErrorPage = lazy(() => import(/* webpackChunkName: "common" */ '../pages/ErrorPage'));
const RegisterPage = lazy(() =>
  import(/* webpackChunkName: "auth" */ '../pages/register/RegisterPage')
);

// Terms pages
const BenefitsPage = lazy(() => import(/* webpackChunkName: "terms" */ '../pages/benefits'));
const TermPage = lazy(() => import(/* webpackChunkName: "terms" */ '../pages/term/TermPage'));
const AccountTermPage = lazy(() =>
  import(/* webpackChunkName: "terms" */ '../pages/term/AccountTermPage')
);
const ClaimTermPage = lazy(() =>
  import(/* webpackChunkName: "terms" */ '../pages/term/ClaimTermPage')
);
const PrivacyPolicyPage = lazy(() =>
  import(/* webpackChunkName: "terms" */ '../pages/term/PrivacyPolicyPage')
);
const ClaimSecurity = lazy(() =>
  import(/* webpackChunkName: "terms" */ '../pages/term/ClaimSecurityPage')
);

// EWI claim pages
const CreateClaimEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/CreatePage.js')
);
const DetailClaimEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/DetailPage.js')
);
const InfoClaimEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/InfoPage.js')
);
const UploadLicenseEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/UploadLicensePage.js')
);
const ContractDetailEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/ContractDetailPage.js')
);

// Device authentication pages
const ShortLinkPage = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/inspection-liberty/ShortLinkPage')
);
const QnA = lazy(() => import(/* webpackChunkName: "common" */ '../pages/qna/QnA'));
const CheckAuthen = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/CheckAuthen')
);
const TutorialDevice = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/TutorialDevice')
);
const ChooseMethod = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/ChooseMethod')
);
const DeviceQrCode = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/DeviceQrCode')
);
const DeviceEmail = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/DeviceEmail')
);
const TakeDevicePhoto = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/TakeDevicePhoto')
);
const InspectSuccess = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/Success')
);

// Take photo device authentication pages
const CheckAuthenTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/CheckAuthen'
  )
);
const ChooseMethodTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/ChooseMethod'
  )
);
const DeviceQrCodeTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/DeviceQrCode'
  )
);
const DeviceEmailTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/DeviceEmail'
  )
);
const TakeDevicePhotoTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/TakeDevicePhoto'
  )
);
const InspectSuccessTakePhoto = lazy(() =>
  import(/* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/Success')
);

// Sale moto pages
const LayoutMoto = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Layout')
);
const PlateNoMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/PlateNo')
);
const ConfirmPlateNoMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/ConfirmPlateNo')
);
const PaymentMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Payment')
);
const Contract2WheelDetailMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Contract2WheelDetail')
);
const SuccessMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Success')
);
const ErrorMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Error')
);
const DrawerMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/drawer')
);

// Sale oto pages
const LayoutOTo = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Layout')
);
const PlateNoOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/PlateNo')
);
const ConfirmPlateNoOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/ConfirmPlateNo')
);
const PaymentOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Payment')
);
const Contract4WheelDetailMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Contract4WheelDetail.js')
);
const SuccessOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Success')
);
const ErrorOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Error')
);

// Car inspection pages
const InspectMoto = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/Inspect.js')
);
const MotoAuthInformation = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/Information.js')
);
const MotoAuthExpertise = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/Expertise.js')
);
const MotoAuthResultMessage = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/ResultMessage.js')
);
const MotoAuthTerm = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/Term.js')
);
const ViewAuthExpertise = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/ViewExpertise.js')
);

// Liberty inspection pages
const InspectionLiberty = lazy(() =>
  import(/* webpackChunkName: "inspection-liberty" */ '../pages/inspection-liberty/Expertise')
);
const InspectionGic = lazy(() =>
  import(/* webpackChunkName: "inspection-liberty" */ '../pages/inspection-gic')
);

// GCN inspection pages
const InspectionGCN = lazy(() =>
  import(/* webpackChunkName: "inspection-gcn" */ '../pages/inspection-gcn')
);

// Insurance car pages
const InsuranceCarInputInformation = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/InputInformation')
);
const InsuranceCarChooseBenefits = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/ChooseBenefits')
);
const InsuranceCarExpertise = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/Expertise')
);
const InsuranceCarInputInformationInsurance = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/InputInformationInsurance')
);
const InsuranceCarReviewInformation = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/ReviewInformation')
);
const InsuranceCarPaymentResult = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/PaymentResult')
);
const InsuranceCarInsuranceList = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/InsuranceList')
);
const InsuranceCarDetailInformation = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/DetailInformationView')
);

// Device authentication TIKI pages
const TakeDevicePhotoTiki = lazy(() =>
  import(/* webpackChunkName: "device-auth-tiki" */ '../pages/device-authen-tiki/TakeDevicePhoto')
);

// Device authentication MOMO pages
const TakeDevicePhotoMomo = lazy(() =>
  import(/* webpackChunkName: "device-auth-momo" */ '../pages/device-authen-momo/TakeDevicePhoto')
);
const TakeDevicePhotoV3 = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-momo" */ '../pages/device-authen-momo/TakeDevicePhotov3.js'
  )
);

// ADLD pages
const ContractADLDPage = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/contract/ContractADLDPage')
);
const ContractADLDDetail = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/contract/ContractADLDDetail')
);
const CreateClaimADLDPage = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/claim/CreateADLDClaim.js')
);
const ClaimDetailADLD = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/claim/ClaimDetailADLD.js')
);
const ClaimInfoADLD = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/claim/ClaimInfoADLD.js')
);

// Thiết bị điện tử pages
const ContractGADDetail = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/contract/ContractGADDetail')
);
const CreateGADClaimPage = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/claim/CreateGADClaim.js')
);
const ClaimDetailGAD = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/claim/ClaimDetailGAD.js')
);
const ClaimGADInfoPage = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/claim/ClaimGADInfo.js')
);
const UploadLicenseGADPage = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/claim/UploadLicenseGAD.js')
);

// GAD AES pages
const CreateGADClaimPageAES = lazy(() =>
  import(/* webpackChunkName: "gad-aes" */ '../pages/claim/CreateGADClaimAES')
);
const ClaimDetailGADPageAES = lazy(() =>
  import(/* webpackChunkName: "gad-aes" */ '../pages/claim/ClaimDetailGADAES')
);
const ClaimGADInfoPageAES = lazy(() =>
  import(/* webpackChunkName: "gad-aes" */ '../pages/claim/ClaimGADInfoAES')
);
const UploadLicenseAES = lazy(() =>
  import(/* webpackChunkName: "gad-aes" */ '../pages/claim/UploadLicenseGADAES')
);

// AMP pages
const CreateAMPClaimPageAES = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/CreateClaim')
);
const ClaimDetailAMPPageAES = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/DetailClaim')
);
const ClaimAMPInfoPageAES = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/InfoClaim')
);
const ClaimAMPPageAES = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/index')
);
const UploadLicenseAmp = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/UploadLicense')
);
const ClaimDetailAMPPage = lazy(() =>
  import(/* webpackChunkName: "amp" */ '../pages/claim/amp/Detail.js')
);
const ClaimCreateAMPPage = lazy(() =>
  import(/* webpackChunkName: "amp" */ '../pages/claim/amp/Create.js')
);
const ClaimInfoAMPPage = lazy(() =>
  import(/* webpackChunkName: "amp" */ '../pages/claim/amp/Info.js')
);
const ClaimUploadLicenseAMPPage = lazy(() =>
  import(/* webpackChunkName: "amp" */ '../pages/claim/amp/UploadLicense.js')
);

// EWI AES pages
const ClaimEWIPageAES = lazy(() =>
  import(/* webpackChunkName: "ewi-aes" */ '../pages/ewi-claim/index')
);

// PCS VPBank pages
const CreatePCSClaimPageAES = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/CreateClaim')
);
const ClaimDetailPCSPageAES = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/DetailClaim')
);
const ClaimPCSInfoPageAES = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/InfoClaim')
);
const ClaimPCSPageAES = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/index')
);
const UploadLicensePCS = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/UploadLicense')
);
const PCSCreateClaimVpbank = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/CreateClaimVpbank')
);

// Chi tiết bảo hiểm xe máy
const Contract2WheelDetail = lazy(() =>
  import(/* webpackChunkName: "2wheel" */ '../pages/contract/Contract2WheelDetail')
);

// Chi tiết yêu cầu bồi thường
const Claim2WheelDetail = lazy(() =>
  import(/* webpackChunkName: "2wheel-claim" */ '../pages/claim/Claim2WheelDetail.js')
);
const ClaimInfo2Wheel = lazy(() =>
  import(/* webpackChunkName: "2wheel-claim" */ '../pages/claim/ClaimInfo2Wheel.js')
);
const VerifySMS = lazy(() =>
  import(/* webpackChunkName: "claim-common" */ '../pages/claim/VerifySMS')
);
const VerifySMSMomo = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/VerifySMSMomo')
);

// Tạo bồi thường xe máy
const CreateClaim2Wheel = lazy(() =>
  import(/* webpackChunkName: "2wheel-claim" */ '../pages/claim/CreateClaim2Wheel.js')
);
const UploadLicense = lazy(() =>
  import(/* webpackChunkName: "claim-common" */ '../pages/claim/UploadLicense.js')
);
const UploadLicenseMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/UploadLicenseMomo')
);

// Chi tiết bảo hiểm oto
const Contract4WheelDetail = lazy(() =>
  import(
    /* webpackChunkName: "4wheel" */ '../pages/contract/Contract4Wheel/Contract4WheelDetail.js'
  )
);
const Contract4WheelAutoCareDetail = lazy(() =>
  import(
    /* webpackChunkName: "4wheel" */ '../pages/contract/Contract4Wheel/Contract4WheelAutoCareDetail.js'
  )
);

// Tạo bồi thường oto
const CreateClaim4Wheel = lazy(() =>
  import(/* webpackChunkName: "4wheel-claim" */ '../pages/claim/4Wheel/CreateClaim4Wheel')
);

// Chi tiết YCBT ô tô
const Claim4WheelDetail = lazy(() =>
  import(/* webpackChunkName: "4wheel-claim" */ '../pages/claim/4Wheel/Claim4WheelDetail.js')
);
const ClaimInfo4Wheel = lazy(() =>
  import(/* webpackChunkName: "4wheel-claim" */ '../pages/claim/4Wheel/ClaimInfo4Wheel.js')
);
const UploadLicense4WheelPage = lazy(() =>
  import(/* webpackChunkName: "4wheel-claim" */ '../pages/claim/4Wheel/UploadLicense.js')
);

// Momo AES
const Claim4WheelDetailMomoPage = lazy(() =>
  import(/* webpackChunkName: "4wheel-momo" */ '../pages/claim/4Wheel/Claim4WheelDetailMomo.js')
);
const Claim4WheelInfoMomoPage = lazy(() =>
  import(/* webpackChunkName: "4wheel-momo" */ '../pages/claim/4Wheel/Claim4WheelInfoMomo.js')
);
const Claim4WheelUploadLicenseMomoPage = lazy(() =>
  import(
    /* webpackChunkName: "4wheel-momo" */ '../pages/claim/4Wheel/Claim4WheelUploadLicenseMomo.js'
  )
);
const Create4WheelClaimMomoPage = lazy(() =>
  import(/* webpackChunkName: "4wheel-momo" */ '../pages/claim/4Wheel/Create4WheelClaimMomo.js')
);

// Health insurance pages
const HealthInsuranceGetRefundInfo = lazy(() =>
  import(/* webpackChunkName: "health-insurance" */ '../pages/health-insurance/GetRefundInfo')
);
const HealthInsurancePayment = lazy(() =>
  import(/* webpackChunkName: "health-insurance" */ '../pages/health-insurance/payment')
);
const HealthInsurancePaymentResult = lazy(() =>
  import(/* webpackChunkName: "health-insurance" */ '../pages/health-insurance/payment/Result.js')
);
const HealthInsurancSupport = lazy(() =>
  import(
    /* webpackChunkName: "health-insurance" */ '../pages/health-insurance/support-request/index.js'
  )
);
const HealthInsuranceCreateSupport = lazy(() =>
  import(
    /* webpackChunkName: "health-insurance" */ '../pages/health-insurance/support-request/Create.js'
  )
);
const HealthInsuranceDetailSupport = lazy(() =>
  import(
    /* webpackChunkName: "health-insurance" */ '../pages/health-insurance/support-request/Detail.js'
  )
);
const HealthInsuranceAddInfoSupport = lazy(() =>
  import(
    /* webpackChunkName: "health-insurance" */ '../pages/health-insurance/support-request/AddInfo.js'
  )
);
const HealthInsuranceDetailEmed = lazy(() =>
  import(/* webpackChunkName: "health-insurance" */ '../pages/health-insurance/payment/Detail')
);

const Routes = () => {
  const token = localStorage.getItem(LocalStorageText.Token);
  const [loginSuccess, setLoginSuccess] = useState(token && token !== InitState);

  useEffect(() => {
    loginObservable.subscribe((token) => {
      setLoginSuccess(token && token !== InitState);
    });
  });

  const guard = (element) => <GuardedRoute element={element} isAuth={loginSuccess} />;

  return (
    <Suspense fallback={<div />}>
      <RouterRoutes>
        <Route
          path="/term-car"
          element={
            <MotoLayout>
              <MotoAuthTerm />
            </MotoLayout>
          }
        />

        <Route
          path="/inspect-car/:codeSnippet"
          element={
            <MotoDefaultLayout>
              <MotoAuthExpertise />
            </MotoDefaultLayout>
          }
        />
        <Route
          path="/result-car/:codeSnippet"
          element={
            <MotoDefaultLayout>
              <MotoAuthResultMessage />
            </MotoDefaultLayout>
          }
        />
        <Route
          path="/view-inspect-car/:codeSnippet"
          element={
            <MotoDefaultLayout>
              <ViewAuthExpertise />
            </MotoDefaultLayout>
          }
        />

        <Route
          path="/health-insurance-get-refund-info/:codeSnippet"
          element={
            <HealthInsuranceLayout>
              <HealthInsuranceGetRefundInfo />
            </HealthInsuranceLayout>
          }
        />
        <Route
          path="/social-insurance/medical-insurance/:codeSnippet"
          element={
            <HealthInsuranceLayout>
              <HealthInsurancePayment />
            </HealthInsuranceLayout>
          }
        />
        <Route
          path="/social-insurance/detail/:codeSnippet"
          element={
            <HealthInsuranceLayout>
              <HealthInsuranceDetailEmed />
            </HealthInsuranceLayout>
          }
        />
        <Route
          path="/health-ins/payment"
          element={
            <HealthInsuranceLayout>
              <HealthInsurancePaymentResult />
            </HealthInsuranceLayout>
          }
        />
        <Route
          path="/inspect-car-coi/:codeSnippet"
          element={
            <HealthInsuranceLayout>
              <InspectionGCN />
            </HealthInsuranceLayout>
          }
        />
        <Route
          path="/health-insurance-support/:codeSnippet"
          element={
            <HealthInsuranceLayout>
              <HealthInsurancSupport />
            </HealthInsuranceLayout>
          }
        />
        <Route
          path="/health-insurance-support-create/:codeSnippet"
          element={
            <HealthInsuranceLayout>
              <HealthInsuranceCreateSupport />
            </HealthInsuranceLayout>
          }
        />
        <Route
          path="/health-insurance-support-detail/:codeSnippet"
          element={
            <HealthInsuranceLayout>
              <HealthInsuranceDetailSupport />
            </HealthInsuranceLayout>
          }
        />
        <Route
          path="/health-insurance-support-add-info/:codeSnippet"
          element={
            <HealthInsuranceLayout>
              <HealthInsuranceAddInfoSupport />
            </HealthInsuranceLayout>
          }
        />

        <Route
          path="/c/:codeSnippet"
          element={
            <CommonLayout>
              <InspectionGic />
            </CommonLayout>
          }
        />
        <Route
          path="/a/:codeSnippet"
          element={
            <CommonLayout>
              <InspectionGic />
            </CommonLayout>
          }
        />

        <Route
          path="/liv-inspect-car/:codeSnippet"
          element={
            <InspectionLibertyLayout>
              <InspectionLiberty />
            </InspectionLibertyLayout>
          }
        />
        <Route
          path="/gic-inspect-car/:codeSnippet"
          element={
            <InspectionLibertyLayout>
              <InspectionLiberty />
            </InspectionLibertyLayout>
          }
        />
        <Route
          path="/g/:slug"
          element={
            <InspectionLibertyLayout>
              <ShortLinkPage />
            </InspectionLibertyLayout>
          }
        />

        <Route
          path="/insurance-car/input-informations/:partnerId"
          element={
            <VatChatOtoLayout>
              <InsuranceCarInputInformation />
            </VatChatOtoLayout>
          }
        />
        <Route
          path="/insurance-car/choose-benefits/:codeSnippet"
          element={
            <VatChatOtoLayout>
              <InsuranceCarChooseBenefits />
            </VatChatOtoLayout>
          }
        />
        <Route
          path="/insurance-car/expertise/:codeSnippet"
          element={
            <VatChatOtoLayout>
              <InsuranceCarExpertise />
            </VatChatOtoLayout>
          }
        />
        <Route
          path="/insurance-car/input-informations-insurance/:codeSnippet"
          element={
            <VatChatOtoLayout>
              <InsuranceCarInputInformationInsurance />
            </VatChatOtoLayout>
          }
        />
        <Route
          path="/insurance-car/review-information/:codeSnippet"
          element={
            <VatChatOtoLayout>
              <InsuranceCarReviewInformation />
            </VatChatOtoLayout>
          }
        />
        <Route
          path="/insurance-car/payment-result/:codeSnippet"
          element={
            <VatChatOtoLayout>
              <InsuranceCarPaymentResult />
            </VatChatOtoLayout>
          }
        />
        <Route
          path="/insurance-car/order-detail/:codeSnippet"
          element={
            <VatChatOtoLayout>
              <InsuranceCarDetailInformation />
            </VatChatOtoLayout>
          }
        />

        <Route
          path="/sale"
          element={
            <SaleLayout>
              <HomeSalePage />
            </SaleLayout>
          }
        />
        <Route
          path="/sale/account"
          element={<SaleLayout>{guard(<AccountPage />)}</SaleLayout>}
        />
        <Route
          path="/sale/user-info"
          element={<SaleLayout>{guard(<UserInfo />)}</SaleLayout>}
        />
        <Route
          path="/sale/password"
          element={<SaleLayout>{guard(<Password />)}</SaleLayout>}
        />
        <Route
          path="/sale/order"
          element={<SaleLayout>{guard(<InsuranceCarInsuranceList />)}</SaleLayout>}
        />
        <Route
          path="/sale/account-term"
          element={
            <SaleLayout>
              <AccountTermPage />
            </SaleLayout>
          }
        />

        <Route
          path="/compulsory-moto/plate-no/:partnerId"
          element={
            <LayoutMoto>
              <PlateNoMotoPage />
            </LayoutMoto>
          }
        />
        <Route
          path="/compulsory-moto/confirm-plate-no/:requestId"
          element={
            <LayoutMoto>
              <ConfirmPlateNoMotoPage />
            </LayoutMoto>
          }
        />
        <Route
          path="/compulsory-moto/payment/:requestId"
          element={
            <LayoutMoto>
              <PaymentMotoPage />
            </LayoutMoto>
          }
        />
        <Route
          path="/compulsory-moto/success/:requestId"
          element={
            <LayoutMoto>
              <SuccessMotoPage />
            </LayoutMoto>
          }
        />
        <Route
          path="/compulsory-moto/contract-detail/:requestId"
          element={
            <LayoutMoto>
              <Contract2WheelDetailMotoPage />
            </LayoutMoto>
          }
        />
        <Route
          path="/compulsory-moto/error/:requestId"
          element={
            <LayoutMoto>
              <ErrorMotoPage />
            </LayoutMoto>
          }
        />
        <Route
          path="/compulsory-moto/drawer/:partnerId"
          element={
            <LayoutMoto>
              <DrawerMotoPage />
            </LayoutMoto>
          }
        />

        <Route
          path="/compulsory-oto/plate-no/:partnerId"
          element={
            <LayoutOTo>
              <PlateNoOtoPage />
            </LayoutOTo>
          }
        />
        <Route
          path="/compulsory-oto/confirm-plate-no/:requestId"
          element={
            <LayoutOTo>
              <ConfirmPlateNoOtoPage />
            </LayoutOTo>
          }
        />
        <Route
          path="/compulsory-oto/payment/:requestId"
          element={
            <LayoutOTo>
              <PaymentOtoPage />
            </LayoutOTo>
          }
        />
        <Route
          path="/compulsory-oto/success/:requestId"
          element={
            <LayoutOTo>
              <SuccessOtoPage />
            </LayoutOTo>
          }
        />
        <Route
          path="/compulsory-oto/contract-detail/:requestId"
          element={
            <LayoutOTo>
              <Contract4WheelDetailMotoPage />
            </LayoutOTo>
          }
        />
        <Route
          path="/compulsory-oto/error/:requestId"
          element={
            <LayoutOTo>
              <ErrorOtoPage />
            </LayoutOTo>
          }
        />

        <Route
          path="/"
          element={
            <AppLayout>
              <HomePage />
            </AppLayout>
          }
        />
        <Route
          path="/home-customer"
          element={
            <AppLayout>
              <HomeCustomerPage />
            </AppLayout>
          }
        />
        <Route path="/claim" element={<AppLayout>{guard(<ClaimPage />)}</AppLayout>} />
        <Route
          path="/claim-detail-pcs/:claimId"
          element={<AppLayout>{guard(<ClaimDetail />)}</AppLayout>}
        />
        <Route
          path="/claim-info/:claimId"
          element={<AppLayout>{guard(<ClaimInfo />)}</AppLayout>}
        />
        <Route
          path="/claim-info-momo/:polMomo"
          element={
            <AppLayout>
              <ClaimInfoMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/claim-momo/:polMomo"
          element={
            <AppLayout>
              <ClaimDetailMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/claim-guide/:policyName"
          element={<AppLayout>{guard(<ClaimGuide />)}</AppLayout>}
        />
        <Route path="/contract" element={<AppLayout>{guard(<ContractPage />)}</AppLayout>} />
        <Route
          path="/contract-detail-pcs/:contractId"
          element={<AppLayout>{guard(<ContractDetail contractType="pcs" />)}</AppLayout>}
        />
        <Route
          path="/create-claim/:contractId"
          element={<AppLayout>{guard(<CreateClaimPage />)}</AppLayout>}
        />
        <Route
          path="/create-claim-momo/:polMomo"
          element={
            <AppLayout>
              <CreateClaimMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/contract-detail-ybb/:contractId"
          element={<AppLayout>{guard(<Contract2WheelDetail />)}</AppLayout>}
        />
        <Route
          path="/claim-detail-ybb/:claimId"
          element={<AppLayout>{guard(<Claim2WheelDetail />)}</AppLayout>}
        />
        <Route
          path="/claim-2-wheel-info/:claimId"
          element={<AppLayout>{guard(<ClaimInfo2Wheel />)}</AppLayout>}
        />
        <Route
          path="/verify-sms/:claimId"
          element={<AppLayout>{guard(<VerifySMS />)}</AppLayout>}
        />
        <Route
          path="/create-claim-2-wheel/:contractId"
          element={<AppLayout>{guard(<CreateClaim2Wheel />)}</AppLayout>}
        />
        <Route
          path="/upload-license/:action/:claimId"
          element={<AppLayout>{guard(<UploadLicense />)}</AppLayout>}
        />
        <Route
          path="/claim-2-wheel-info-momo/:polMomo"
          element={
            <AppLayout>
              <ClaimInfo2WheelMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/upload-license-momo/:action/:polMomo"
          element={
            <AppLayout>
              <UploadLicenseMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/2wheel-claim-momo/:polMomo"
          element={
            <AppLayout>
              <Claim2WheelDetailMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/create-2-wheel-claim-momo/:polMomo"
          element={
            <AppLayout>
              <CreateClaim2WheelMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/verify-sms-momo/:polMomo"
          element={
            <AppLayout>
              <VerifySMSMomo />
            </AppLayout>
          }
        />
        <Route
          path="/contract-detail-yab/:id"
          element={<AppLayout>{guard(<Contract4WheelDetail />)}</AppLayout>}
        />
        <Route
          path="/contract-detail-auto/:id"
          element={<AppLayout>{guard(<Contract4WheelAutoCareDetail />)}</AppLayout>}
        />
        <Route
          path="/create-claim-4-wheel/:contractId"
          element={<AppLayout>{guard(<CreateClaim4Wheel />)}</AppLayout>}
        />
        <Route
          path="/claim-detail-yab/:claimId"
          element={<AppLayout>{guard(<Claim4WheelDetail />)}</AppLayout>}
        />
        <Route
          path="/upload-license-4-wheel/:action/:claimId"
          element={<AppLayout>{guard(<UploadLicense4WheelPage />)}</AppLayout>}
        />
        <Route
          path="/claim-4-wheel-info/:claimId"
          element={<AppLayout>{guard(<ClaimInfo4Wheel />)}</AppLayout>}
        />
        <Route
          path="/4wheel-claim-create-mono/:polMomo"
          element={
            <AppLayout>
              <Create4WheelClaimMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/4wheel-claim/:polMomo"
          element={
            <AppLayout>
              <Claim4WheelDetailMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/4wheel-claim-info-momo/:polMomo"
          element={
            <AppLayout>
              <Claim4WheelInfoMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/4wheel-claim-upload-license-momo/:action/:polMomo"
          element={
            <AppLayout>
              <Claim4WheelUploadLicenseMomoPage />
            </AppLayout>
          }
        />
        <Route
          path="/contract-adld"
          element={<AppLayout>{guard(<ContractADLDPage />)}</AppLayout>}
        />
        <Route
          path="/contract-detail-adld/:contractId"
          element={<AppLayout>{guard(<ContractADLDDetail />)}</AppLayout>}
        />
        <Route
          path="/create-claim-adld/:contractId"
          element={<AppLayout>{guard(<CreateClaimADLDPage />)}</AppLayout>}
        />
        <Route
          path="/claim-detail-adld/:claimId"
          element={<AppLayout>{guard(<ClaimDetailADLD />)}</AppLayout>}
        />
        <Route
          path="/claim-info-adld/:claimId"
          element={<AppLayout>{guard(<ClaimInfoADLD />)}</AppLayout>}
        />
        <Route
          path="/contract-detail-epi/:contractId"
          element={<AppLayout>{guard(<ContractGADDetail />)}</AppLayout>}
        />
        <Route
          path="/create-claim-ep/:contractId"
          element={<AppLayout>{guard(<CreateGADClaimPage />)}</AppLayout>}
        />
        <Route
          path="/claim-detail-epi/:claimId"
          element={<AppLayout>{guard(<ClaimDetailGAD />)}</AppLayout>}
        />
        <Route
          path="/claim-info-ep/:claimId"
          element={<AppLayout>{guard(<ClaimGADInfoPage />)}</AppLayout>}
        />
        <Route
          path="/upload-license-ep/:action/:claimId"
          element={<AppLayout>{guard(<UploadLicenseGADPage />)}</AppLayout>}
        />
        <Route
          path="/ep-claim-create/:polNo"
          element={
            <AppLayout>
              <CreateGADClaimPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/ep-claim/:polNo"
          element={
            <AppLayout>
              <ClaimDetailGADPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/ep-claim-info/:polNo"
          element={
            <AppLayout>
              <ClaimGADInfoPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/ep-claim-upload-license/:action/:polNo"
          element={
            <AppLayout>
              <UploadLicenseAES />
            </AppLayout>
          }
        />
        <Route
          path="/amp-claim/:codeSnippet"
          element={
            <AppLayout>
              <ClaimAMPPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/amp-claim-create/:codeSnippet"
          element={
            <AppLayout>
              <CreateAMPClaimPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/amp-claim-detail/:codeSnippet"
          element={
            <AppLayout>
              <ClaimDetailAMPPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/amp-claim-info/:codeSnippet"
          element={
            <AppLayout>
              <ClaimAMPInfoPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/amp-claim-upload-license/:action/:polNo"
          element={
            <AppLayout>
              <UploadLicenseAmp />
            </AppLayout>
          }
        />
        <Route
          path="/claim-detail-amp/:claimId"
          element={<AppLayout>{guard(<ClaimDetailAMPPage />)}</AppLayout>}
        />
        <Route
          path="/amp-app-claim-create/:claimId"
          element={<AppLayout>{guard(<ClaimCreateAMPPage />)}</AppLayout>}
        />
        <Route
          path="/amp-app-claim-info/:claimId"
          element={<AppLayout>{guard(<ClaimInfoAMPPage />)}</AppLayout>}
        />
        <Route
          path="/amp-app-claim-upload-license/:action/:polNo"
          element={<AppLayout>{guard(<ClaimUploadLicenseAMPPage />)}</AppLayout>}
        />
        <Route
          path="/contract-detail-amp/:contractId"
          element={
            <AppLayout>
              {guard(
                <ContractDetail
                  getApi="get-detail-policy-amp"
                  createPage="amp-app-claim-create"
                  contractType="amp"
                />
              )}
            </AppLayout>
          }
        />
        <Route
          path="/ewi-claim/:codeSnippet"
          element={
            <AppLayout>
              <ClaimEWIPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/contract-detail-ewi/:contractId"
          element={
            <AppLayout>
              {guard(
                <ContractDetailEwi
                  getApi="get-detail-policy-ewi"
                  createPage="create-claim-ewi"
                  contractType="ewi"
                />
              )}
            </AppLayout>
          }
        />
        <Route
          path="/create-claim-ewi/:claimId"
          element={<AppLayout>{guard(<CreateClaimEwi />)}</AppLayout>}
        />
        <Route
          path="/claim-detail-ewi/:claimId"
          element={<AppLayout>{guard(<DetailClaimEwi />)}</AppLayout>}
        />
        <Route
          path="/claim-info-ewi/:claimId"
          element={<AppLayout>{guard(<InfoClaimEwi />)}</AppLayout>}
        />
        <Route
          path="/upload-license-ewi/:action/:polNo"
          element={<AppLayout>{guard(<UploadLicenseEwi />)}</AppLayout>}
        />
        <Route
          path="/pcs-claim/:codeSnippet"
          element={
            <AppLayout>
              <ClaimPCSPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/pcs-claim-create/:codeSnippet"
          element={
            <AppLayout>
              <CreatePCSClaimPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/pcs-claim-create-vpbank/:codeSnippet"
          element={
            <AppLayout>
              <PCSCreateClaimVpbank />
            </AppLayout>
          }
        />
        <Route
          path="/pcs-claim-detail/:codeSnippet"
          element={
            <AppLayout>
              <ClaimDetailPCSPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/pcs-claim-detail-vpbank/:codeSnippet"
          element={
            <AppLayout>
              <ClaimDetailPCSPageAES getApi="get-detail-claim-pcs" />
            </AppLayout>
          }
        />
        <Route
          path="/pcs-claim-info/:codeSnippet"
          element={
            <AppLayout>
              <ClaimPCSInfoPageAES />
            </AppLayout>
          }
        />
        <Route
          path="/pcs-claim-upload-license/:action/:polNo"
          element={
            <AppLayout>
              <UploadLicensePCS />
            </AppLayout>
          }
        />
        <Route
          path="/confirm-email"
          element={<AppLayout>{guard(<ConfirmEmail />)}</AppLayout>}
        />
        <Route
          path="/verify-email"
          element={<AppLayout>{guard(<VerifyEmail />)}</AppLayout>}
        />
        <Route path="/user-info" element={<AppLayout>{guard(<UserInfo />)}</AppLayout>} />
        <Route path="/password" element={<AppLayout>{guard(<Password />)}</AppLayout>} />
        <Route path="/account" element={<AppLayout>{guard(<AccountPage />)}</AppLayout>} />
        <Route
          path="/notification"
          element={<AppLayout>{guard(<NotificationPage />)}</AppLayout>}
        />
        <Route
          path="/benefits/:policyName"
          element={<AppLayout>{guard(<BenefitsPage />)}</AppLayout>}
        />
        <Route path="/term" element={<AppLayout>{guard(<TermPage />)}</AppLayout>} />
        <Route
          path="/privacy-policy"
          element={<AppLayout>{guard(<PrivacyPolicyPage />)}</AppLayout>}
        />
        <Route
          path="/claim-term"
          element={
            <AppLayout>
              <ClaimTermPage />
            </AppLayout>
          }
        />
        <Route
          path="/qna"
          element={
            <AppLayout>
              <QnA />
            </AppLayout>
          }
        />
        <Route
          path="/account-term"
          element={
            <AppLayout>
              <AccountTermPage />
            </AppLayout>
          }
        />
        <Route
          path="/chinh-sach-bao-mat-du-lieu-ca-nhan"
          element={
            <AppLayout>
              <ClaimSecurity />
            </AppLayout>
          }
        />
        <Route
          path="/check-authen"
          element={
            <AppLayout>
              <CheckAuthen />
            </AppLayout>
          }
        />
        <Route
          path="/tutorial-device"
          element={
            <AppLayout>
              <TutorialDevice />
            </AppLayout>
          }
        />
        <Route
          path="/tutorial-device/:redirectPath"
          element={
            <AppLayout>
              <TutorialDevice />
            </AppLayout>
          }
        />
        <Route
          path="/choose-method"
          element={
            <AppLayout>
              <ChooseMethod />
            </AppLayout>
          }
        />
        <Route
          path="/qr-device"
          element={
            <AppLayout>
              <DeviceQrCode />
            </AppLayout>
          }
        />
        <Route
          path="/email-device"
          element={
            <AppLayout>
              <DeviceEmail />
            </AppLayout>
          }
        />
        <Route
          path="/time-inspect-policy/:codeSnippet"
          element={
            <AppLayout>
              <TakeDevicePhoto />
            </AppLayout>
          }
        />
        <Route
          path="/inspect-success"
          element={
            <AppLayout>
              <InspectSuccess />
            </AppLayout>
          }
        />
        <Route
          path="/inspect-tiki/:codeSnippet"
          element={
            <AppLayout>
              <TakeDevicePhotoTiki />
            </AppLayout>
          }
        />
        <Route
          path="/inspect/:codeSnippet"
          element={
            <AppLayout>
              <TakeDevicePhotoMomo />
            </AppLayout>
          }
        />
        <Route
          path="/inspect-phone-v3/:codeSnippet"
          element={
            <AppLayout>
              <TakeDevicePhotoV3 />
            </AppLayout>
          }
        />
        <Route
          path="/check-authen-take-photo"
          element={
            <AppLayout>
              <CheckAuthenTakePhoto />
            </AppLayout>
          }
        />
        <Route
          path="/choose-method-take-photo"
          element={
            <AppLayout>
              <ChooseMethodTakePhoto />
            </AppLayout>
          }
        />
        <Route
          path="/qr-device-take-photo"
          element={
            <AppLayout>
              <DeviceQrCodeTakePhoto />
            </AppLayout>
          }
        />
        <Route
          path="/email-device-take-photo"
          element={
            <AppLayout>
              <DeviceEmailTakePhoto />
            </AppLayout>
          }
        />
        <Route
          path="/time-inspect-policy-take-photo/:codeSnippet"
          element={
            <AppLayout>
              <TakeDevicePhotoTakePhoto />
            </AppLayout>
          }
        />
        <Route
          path="/inspect-success-take-photo"
          element={
            <AppLayout>
              <InspectSuccessTakePhoto />
            </AppLayout>
          }
        />
        <Route
          path="/login"
          element={
            <AppLayout>
              <LoginPage />
            </AppLayout>
          }
        />
        <Route
          path="/register"
          element={
            <AppLayout>
              <RegisterPage />
            </AppLayout>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <AppLayout>
              <ForgotPasswordPage />
            </AppLayout>
          }
        />
        <Route
          path="/change-password/:phone/:tokenKey"
          element={
            <AppLayout>
              <ChangePasswordPage />
            </AppLayout>
          }
        />
        <Route
          path="*"
          element={
            <AppLayout>
              <ErrorPage status={404} message="Page not found." />
            </AppLayout>
          }
        />
      </RouterRoutes>
    </Suspense>
  );
};

export default Routes;
